/**
 * ProjectCard molecule — card for the home page grid. Three lifecycle states:
 * "active" (default, clickable, opens the canvas), "archived", and "deleted".
 * Non-active states render the body as an inert `<button>` instead of a
 * `<Link>` — an archived/deleted project MUST NOT open the canvas, so there
 * is no href anywhere in that render. `onInertClick` is the only thing a
 * click on a non-active card does.
 *
 * The kebab menu has two sources that compose: `menuItems` (the owner's
 * state-specific actions, e.g. Unarchive/Restore) and the legacy `onDelete`
 * prop, which synthesises a single "Delete…" item wired to the delete dialog
 * below (its `DialogTitle` reads "Delete project"). When the `project-archive`
 * flag is OFF, this is the only delete path; the dialog describes only what
 * `projects.softDelete` does (hides the row; no restore product path exists).
 * Passing both appends the synthesised delete item after `menuItems`, so an
 * owner's archived card can offer Unarchive AND Delete… through the one
 * dialog. Neither prop passed means no menu button at all.
 *
 * Renders the project owner's avatar + name in the card footer so dashboard
 * tiles surface ownership for shared-with-me projects (LEV-643).
 *
 * `sharedWithLabel` only renders when `isOwnedByViewer` is true: a card the
 * viewer does not own already shows the plain "Shared · " marker, and that
 * branch must stay the owner-less default regardless of this prop.
 */
"use client";

import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { formatShortDate } from "@/lib/utils/shared";
import { MoreVertical, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/molecules/ui/Dialog";
import { Button } from "@/components/atoms/primitives/Button";
import { Badge } from "@/components/atoms/primitives/Badge";

export interface ProjectOwnerSummary {
  id: string;
  name: string | null;
  email: string | null;
  imageUrl: string | null;
}

export type ProjectCardState = "active" | "archived" | "deleted";

export interface ProjectCardMenuItem {
  key: string;
  label: string;
  /** Styles the item's text rose, for Delete-shaped actions. */
  destructive?: boolean;
  onSelect: () => void;
}

export interface ProjectCardProps {
  id: string;
  name: string;
  updatedAt: Date | string;
  className?: string;
  /** Legacy: viewer initials (used as a fallback when `owner` isn't provided). */
  initials?: string;
  /** Owner of the project. Drives the avatar + secondary line on the card. */
  owner?: ProjectOwnerSummary;
  /** When true, the byline shows "You" instead of the owner's full name. */
  isOwnedByViewer?: boolean;
  /** "Shared with Ben" etc. (from `sharedWithLine`). Only rendered when `isOwnedByViewer` is true — a collaborator's card keeps the plain "Shared · " marker instead. */
  sharedWithLabel?: string;
  index?: number;
  currentStepLabel?: string;
  /** Lifecycle state. Non-"active" cards never open the canvas (see file header). */
  state?: ProjectCardState;
  /** Chip text shown under the byline, e.g. "Archived 3 weeks ago". Omit to render no chip. */
  chipLabel?: string;
  /** Owner's state-specific menu actions (e.g. Unarchive, Restore). Combines with `onDelete` — see file header. */
  menuItems?: ProjectCardMenuItem[];
  /** Called when a non-"active" card's body is clicked, since it never navigates. */
  onInertClick?: () => void;
  /** When provided, card shows kebab menu with Delete…; called with projectId on confirm. */
  onDelete?: (projectId: string) => void | Promise<void>;
}

function ownerInitials(owner: ProjectOwnerSummary): string {
  const source = owner.name?.trim() || owner.email?.trim() || "";
  if (!source) return "PR";
  const parts = source.split(/\s+|@|\./).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("") || "PR"
  );
}

export function ownerDisplayName(owner: ProjectOwnerSummary): string {
  return owner.name?.trim() || owner.email?.trim() || "Unknown";
}

export function ProjectCard({
  id,
  name,
  updatedAt,
  className,
  initials,
  owner,
  isOwnedByViewer,
  sharedWithLabel,
  index = 0,
  currentStepLabel,
  state = "active",
  chipLabel,
  menuItems,
  onInertClick,
  onDelete,
}: ProjectCardProps) {
  const [navigating, setNavigating] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInert = state !== "active";
  // onDelete's item always comes last — see file header for why both can be present.
  const resolvedMenuItems: ProjectCardMenuItem[] = menuItems ? [...menuItems] : [];
  if (onDelete) {
    resolvedMenuItems.push({
      key: "delete",
      label: "Delete…",
      destructive: true,
      onSelect: () => setDialogOpen(true),
    });
  }
  const showMenuButton = resolvedMenuItems.length > 0;

  useLayoutEffect(() => {
    if (!dropdownOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.right - 140,
    });
  }, [dropdownOpen]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (triggerRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setDropdownOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 4, left: rect.right - 140 });
    };
    document.addEventListener("mousedown", onDocClick, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", onDocClick, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [dropdownOpen]);

  const updatedLabel = formatShortDate(updatedAt);
  const derivedInitials = owner
    ? ownerInitials(owner)
    : initials ||
      name
        .split(/\s+/)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() ?? "")
        .join("") ||
      "PR";
  const ownerName = owner ? ownerDisplayName(owner) : null;
  const ownerLabel = owner ? (isOwnedByViewer ? "You" : ownerName) : null;

  const thumbnailIndex = (index % 10) + 1;
  const thumbnailSrc = `/thumbnails/thumbnail-${thumbnailIndex}.png`;

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(id);
      setDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const body = (
    <>
      <div className="px-3 pt-3">
        <div
          className={cn(
            "relative aspect-[16/9] w-full overflow-hidden rounded-2xl ring-1 ring-slate-200",
            isInert && "opacity-55",
          )}
        >
          <Image
            src={thumbnailSrc}
            alt="Project thumbnail"
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            className={cn("object-cover", isInert && "grayscale")}
            priority={false}
          />
          {navigating && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <Loader2 className="h-6 w-6 animate-spin text-lev-orange" />
            </div>
          )}
        </div>
      </div>
      <div
        className={cn(
          "flex items-center justify-between gap-2 px-4 py-3",
          isInert && "opacity-55",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* Avatar is decorative — the owner's name renders in the
              byline below, so screen readers don't need to read it
              twice. Alt stays empty and aria-hidden marks the
              initials fallback decorative too. */}
          {owner?.imageUrl ? (
            <Image
              src={owner.imageUrl}
              alt=""
              width={32}
              height={32}
              className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <div
              aria-hidden
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#C084FC] text-[11px] font-semibold text-white"
            >
              {derivedInitials}
            </div>
          )}
          <div className="min-w-0">
            <div className="line-clamp-1 text-sm font-medium text-slate-900">
              {name}
            </div>
            <div className="text-xs text-slate-500">
              {ownerLabel && (
                <span className="text-slate-400">{ownerLabel} · </span>
              )}
              {!isOwnedByViewer && (
                <span className="text-blue-500">Shared · </span>
              )}
              {isOwnedByViewer && sharedWithLabel && (
                <span className="text-blue-500">{sharedWithLabel} · </span>
              )}
              {currentStepLabel && <span className="text-slate-400">{currentStepLabel} · </span>}
              Edited {updatedLabel}
            </div>
            {chipLabel && (
              <Badge tone={state === "deleted" ? "danger" : "warning"} className="mt-1">
                {chipLabel}
              </Badge>
            )}
          </div>
        </div>
        {showMenuButton && <div className="w-10 flex-shrink-0" aria-hidden="true" />}
      </div>
    </>
  );

  return (
    <>
      <div
        className={cn(
          "group relative block overflow-visible rounded-[22px] bg-white ring-1 ring-slate-200 shadow-[0_8px_28px_rgba(2,8,23,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_48px_rgba(2,8,23,0.14)]",
          className,
        )}
      >
        {isInert ? (
          <button
            type="button"
            className="block w-full text-left outline-none"
            aria-label={chipLabel ? `${name}, ${chipLabel}` : `${name}, ${state}`}
            onClick={onInertClick}
          >
            {body}
          </button>
        ) : (
          <Link
            href={`/${id}`}
            className="block outline-none"
            aria-label={`Open project ${name}`}
            onClick={(e) => {
              if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) setNavigating(true);
            }}
          >
            {body}
          </Link>
        )}
        {showMenuButton && (
          <div
            className="absolute right-4 bottom-3 z-card-above"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              ref={triggerRef}
              type="button"
              aria-label="Project options"
              aria-expanded={dropdownOpen}
              aria-haspopup="menu"
              className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDropdownOpen((o) => !o);
              }}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {dropdownOpen &&
              typeof document !== "undefined" &&
              createPortal(
                <div
                  ref={dropdownRef}
                  className="fixed z-dropdown min-w-[140px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                  role="menu"
                  style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {resolvedMenuItems.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      role="menuitem"
                      className={cn(
                        "w-full px-3 py-2 text-left text-sm hover:bg-slate-100",
                        item.destructive ? "text-rose-700" : "text-slate-700",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDropdownOpen(false);
                        item.onSelect();
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>,
                document.body,
              )}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{name}&quot;? It disappears from your
              projects, and you will not be able to open it again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setDialogOpen(false)}
              disabled={deleting}
            >
              No
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Yes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
